// Segment-local cylindrical kinematics.
//
// A segment's axis is a cubic Bezier curve from p0 (s=0) to p3
// (s=length). v0 emits *degenerate* beziers — collinear control
// points so the curve is a straight line — and this file evaluates
// them as straight lines for kinematics. When non-degenerate
// beziers land (curved branches), `segmentToWorld` will need to
// integrate the tangent; the squirrel coordinate `(s, θ, r)` stays
// invariant (per ADR-0004 D5).
//
// World-space mapping:
//   t = s / length             // parametric position 0..1 along axis
//   axisPoint = lerp(p0, p3, t)
//   radius   = lerp(radiusBase, radiusTip, t)
//   world    = axisPoint + (v*cos(θ) + w*sin(θ)) * (radius + r)
//
// `r` is offset from the surface — r=0 means flat on the bark.

export interface SegmentDef {
  p0X: number; p0Y: number; p0Z: number;
  p1X: number; p1Y: number; p1Z: number;
  p2X: number; p2Y: number; p2Z: number;
  p3X: number; p3Y: number; p3Z: number;
  length: number;
  radiusBase: number;
  radiusTip: number;
}

type Vec3 = [number, number, number];

function normalize(v: Vec3): Vec3 {
  const len = Math.hypot(v[0], v[1], v[2]);
  if (len === 0) return [0, 0, 0];
  return [v[0] / len, v[1] / len, v[2] / len];
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

/** Unit tangent at the start of the segment (matches the straight
 *  axis for degenerate beziers). */
function segmentTangent(seg: SegmentDef): Vec3 {
  return normalize([seg.p3X - seg.p0X, seg.p3Y - seg.p0Y, seg.p3Z - seg.p0Z]);
}

export function segmentBasis(seg: SegmentDef): { u: Vec3; v: Vec3; w: Vec3 } {
  const u = segmentTangent(seg);
  const helper: Vec3 = Math.abs(u[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
  const v = normalize(cross(u, helper));
  const w = cross(u, v);
  return { u, v, w };
}

export function segmentToWorld(
  seg: SegmentDef,
  s: number,
  theta: number,
  r: number
): Vec3 {
  const { v, w } = segmentBasis(seg);
  const t = seg.length > 0 ? s / seg.length : 0;
  const ax = seg.p0X + (seg.p3X - seg.p0X) * t;
  const ay = seg.p0Y + (seg.p3Y - seg.p0Y) * t;
  const az = seg.p0Z + (seg.p3Z - seg.p0Z) * t;
  const radius = seg.radiusBase + (seg.radiusTip - seg.radiusBase) * t + r;
  const c = Math.cos(theta);
  const sn = Math.sin(theta);
  return [
    ax + (v[0] * c + w[0] * sn) * radius,
    ay + (v[1] * c + w[1] * sn) * radius,
    az + (v[2] * c + w[2] * sn) * radius,
  ];
}

// Outward radial direction at a given theta (unit vector pointing away from axis).
export function radialOutward(seg: SegmentDef, theta: number): Vec3 {
  const { v, w } = segmentBasis(seg);
  const c = Math.cos(theta);
  const sn = Math.sin(theta);
  return [v[0] * c + w[0] * sn, v[1] * c + w[1] * sn, v[2] * c + w[2] * sn];
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// Ray–cylinder intersection in segment-local terms. Treats the segment as
// the finite cylinder defined by its straight axis (p0 → p3) and uses
// `radiusBase` as the cylinder radius. Returns the segment-local theta of
// the nearest front-face hit, or null when the ray misses or hits outside
// [0, length].
//
// v0 emits degenerate beziers so the straight-axis assumption matches
// the rendered geometry. Per-segment taper is small enough that using
// radiusBase instead of solving a truncated-cone equation is acceptable
// for touch targeting; future spec can do exact tapered-cone raycast.
export function raycastSegmentTheta(
  origin: Vec3,
  direction: Vec3,
  seg: SegmentDef
): number | null {
  const { u, v, w } = segmentBasis(seg);
  const radius = seg.radiusBase;
  const dx = origin[0] - seg.p0X;
  const dy = origin[1] - seg.p0Y;
  const dz = origin[2] - seg.p0Z;
  const deltaAxial = dx * u[0] + dy * u[1] + dz * u[2];
  const deltaPerp: Vec3 = [
    dx - deltaAxial * u[0],
    dy - deltaAxial * u[1],
    dz - deltaAxial * u[2],
  ];
  const dirAxial =
    direction[0] * u[0] + direction[1] * u[1] + direction[2] * u[2];
  const dirPerp: Vec3 = [
    direction[0] - dirAxial * u[0],
    direction[1] - dirAxial * u[1],
    direction[2] - dirAxial * u[2],
  ];
  const a =
    dirPerp[0] * dirPerp[0] +
    dirPerp[1] * dirPerp[1] +
    dirPerp[2] * dirPerp[2];
  if (a < 1e-12) return null; // ray parallel to axis
  const b =
    2 *
    (deltaPerp[0] * dirPerp[0] +
      deltaPerp[1] * dirPerp[1] +
      deltaPerp[2] * dirPerp[2]);
  const c =
    deltaPerp[0] * deltaPerp[0] +
    deltaPerp[1] * deltaPerp[1] +
    deltaPerp[2] * deltaPerp[2] -
    radius * radius;
  const disc = b * b - 4 * a * c;
  if (disc < 0) return null;
  const sq = Math.sqrt(disc);
  const t1 = (-b - sq) / (2 * a);
  const t2 = (-b + sq) / (2 * a);
  let t: number;
  if (t1 > 0) t = t1;
  else if (t2 > 0) t = t2;
  else return null;
  const sHit = deltaAxial + t * dirAxial;
  if (sHit < 0 || sHit > seg.length) return null;
  const hx = deltaPerp[0] + t * dirPerp[0];
  const hy = deltaPerp[1] + t * dirPerp[1];
  const hz = deltaPerp[2] + t * dirPerp[2];
  const vDot = hx * v[0] + hy * v[1] + hz * v[2];
  const wDot = hx * w[0] + hy * w[1] + hz * w[2];
  return Math.atan2(wDot, vDot);
}

// Off-trunk fallback for touch positioning. When the ray misses the
// segment cylinder (raycastSegmentTheta → null) this classifies the
// miss and, if the finger is past the trunk silhouette, returns the
// sign of theta-rotation that takes the squirrel around the trunk in
// the direction of the finger:
//
//   0  → ray hits the infinite cylinder (perpDist ≤ radius). Finger
//        is above/below the segment, not past the silhouette. Caller
//        should hold θ.
//   ±1 → ray is past the silhouette (perpDist > radius). Caller
//        should rotate at SPIRAL_SPEED in this direction; the sign is
//        independent of cur.theta, so a finger held past the
//        silhouette keeps the squirrel rotating around the back of
//        the trunk at a steady rate.
//
// The sign is determined by the 2D cross-product (in the segment's
// (v, w) basis) between the camera's perpendicular position and the
// ray's perpendicular-foot direction. Positive cross ⇒ foot is CCW
// from camera ⇒ +θ rotation; negative ⇒ −θ.
export function raySegmentSaturationDirection(
  origin: Vec3,
  direction: Vec3,
  cameraPos: Vec3,
  seg: SegmentDef
): -1 | 0 | 1 {
  const { u, v, w } = segmentBasis(seg);
  const radius = seg.radiusBase;
  const dx = origin[0] - seg.p0X;
  const dy = origin[1] - seg.p0Y;
  const dz = origin[2] - seg.p0Z;
  const dA = dx * u[0] + dy * u[1] + dz * u[2];
  const dpX = dx - dA * u[0];
  const dpY = dy - dA * u[1];
  const dpZ = dz - dA * u[2];
  const dirA =
    direction[0] * u[0] + direction[1] * u[1] + direction[2] * u[2];
  const drX = direction[0] - dirA * u[0];
  const drY = direction[1] - dirA * u[1];
  const drZ = direction[2] - dirA * u[2];
  const a = drX * drX + drY * drY + drZ * drZ;
  if (a < 1e-12) return 0; // ray parallel to axis
  const t = Math.max(0, -(dpX * drX + dpY * drY + dpZ * drZ) / a);
  const fX = dpX + t * drX;
  const fY = dpY + t * drY;
  const fZ = dpZ + t * drZ;
  const perpDist = Math.hypot(fX, fY, fZ);
  if (perpDist <= radius) return 0; // above/below — caller holds
  const cx = cameraPos[0] - seg.p0X;
  const cy = cameraPos[1] - seg.p0Y;
  const cz = cameraPos[2] - seg.p0Z;
  const cA = cx * u[0] + cy * u[1] + cz * u[2];
  const cpX = cx - cA * u[0];
  const cpY = cy - cA * u[1];
  const cpZ = cz - cA * u[2];
  const camV = cpX * v[0] + cpY * v[1] + cpZ * v[2];
  const camW = cpX * w[0] + cpY * w[1] + cpZ * w[2];
  const footV = fX * v[0] + fY * v[1] + fZ * v[2];
  const footW = fX * w[0] + fY * w[1] + fZ * w[2];
  const cross = camV * footW - camW * footV;
  if (cross > 1e-9) return 1;
  if (cross < -1e-9) return -1;
  return 0;
}
